import { FulfilableBasketItem } from '@features/AddedToBasket/Fulfilable/FulfilableAddedToBasket.types';
import moment from 'moment';
import { Item } from 'shared-types';
import type { TranslationsFunc } from '@lang';

type BaseItineraryItem = {
  time: string;
  name: string;
  parent?: string;
};

type RoomCheckinItinerary = {
  type: 'room-checkin';
} & BaseItineraryItem;

type RoomCheckoutItinerary = {
  type: 'room-checkout';
} & BaseItineraryItem;

type SpaItinerary = {
  type: 'spa';
} & BaseItineraryItem;

type DiningItinerary = {
  type: 'dining';
} & BaseItineraryItem;

export type ItineraryItem =
  | RoomCheckinItinerary
  | RoomCheckoutItinerary
  | SpaItinerary
  | DiningItinerary;

export type DailyItinerary = {
  [date: string]: ItineraryItem[];
};

export type GuestItinerary = {
  guestNumber: number;
  guestName: string;
  items: DailyItinerary;
  hasItems: boolean;
};

export function getItineraryItems(
  items: Item[] | FulfilableBasketItem[],
  t: TranslationsFunc,
  labels?: Record<string, string>
): GuestItinerary[] {
  const formatDate = (date: string) => moment(date).format('DD MMMM YYYY');
  const formatTime = (date: string) => moment(date).format('HH:mm');
  const guestMap = new Map<string, GuestItinerary>();

  const formatGuestName = (guest: { title?: string; firstName: string; lastName: string }) => {
    const title = guest.title ? `${guest.title.toLocaleUpperCase()} ` : '';
    return `${title}${guest.firstName.toLocaleUpperCase()} ${guest.lastName.toLocaleUpperCase()}`;
  };

  const nonFulfilableItems: Item[] = items.filter(
    (item) => !('fulfilmentStatus' in item)
  ) as Item[];

  const handlers = {
    rooms: (item: Item) => {
      const roomLabel = [item.bookable?.name, item.bookable?.rate].filter(Boolean).join(' · ');
      const roomItems: (Omit<ItineraryItem, 'date'> & { date: string })[] = [
        {
          type: 'room-checkin',
          date: formatDate(item.checkIn || ''),
          time: item.metadata?.checkInTime || '',
          name: t('basket.arrival'),
          parent: roomLabel,
        },
        {
          type: 'room-checkout',
          date: formatDate(item.checkOut || ''),
          time: item.metadata?.checkOutTime || '',
          name: t('basket.departure'),
          parent: roomLabel,
        },
      ];

      item.guestDetails?.forEach((guest) => {
        const guestKey = getGuestKey(guest.title, guest.firstName, guest.lastName);
        const guestItinerary = guestMap.get(guestKey);
        if (guestItinerary) {
          roomItems.forEach((item) => {
            const { date, ...itemWithoutDate } = item;
            guestItinerary.items[date] = guestItinerary.items[date] || [];
            guestItinerary.items[date].push(itemWithoutDate);
          });
        }
      });
    },

    spa: (item: Item) => {
      if (item.type !== 'spa-packages') {
        const spaItem: Omit<SpaItinerary, 'date'> & { date: string } = {
          type: 'spa',
          date: formatDate(item.checkIn || ''),
          name: item.bookable?.name || '',
          time: formatTime(item.checkIn || ''),
          parent: item.bookable?.name || '',
        };

        item.guestDetails?.forEach((guest) => {
          const guestKey = getGuestKey(guest.title, guest.firstName, guest.lastName);
          const guestItinerary = guestMap.get(guestKey);
          if (guestItinerary) {
            const { date, ...itemWithoutDate } = spaItem;
            guestItinerary.items[date] = guestItinerary.items[date] || [];
            guestItinerary.items[date].push(itemWithoutDate);
          }
        });
      } else if (item.type === 'spa-packages') {
        item.selectionGroups
          ?.filter((group) => !group?.isExtras)
          .forEach((group) => {
            group.selections.forEach((selection) => {
              if (selection.guestDetails) {
                const guestKey = getGuestKey(
                  selection.guestDetails.title ?? '',
                  selection.guestDetails.firstName,
                  selection.guestDetails.lastName
                );
                const guestItinerary = guestMap.get(guestKey);
                if (guestItinerary) {
                  const spaItem: Omit<SpaItinerary, 'date'> & { date: string } = {
                    type: 'spa',
                    date: formatDate(selection.startDate || item.checkIn || ''),
                    name: selection.name || '',
                    time: selection.startTime || '',
                    parent: item.spaPackage?.name || item.bookable?.name || '',
                  };
                  const { date, ...itemWithoutDate } = spaItem;
                  guestItinerary.items[date] = guestItinerary.items[date] || [];
                  guestItinerary.items[date].push(itemWithoutDate);
                }
              }
            });
          });
      }
    },

    tables: (item: Item) => {
      const dateTime = item.checkIn || '';
      const formattedDate = formatDate(dateTime);
      const diningItem: Omit<DiningItinerary, 'date'> = {
        type: 'dining',
        time: formatTime(dateTime),
        name: labels?.tables || t('basketDrawer.dining'),
        parent: item.bookable?.ids?.restaurantName || '',
      };

      const sortedGuestItineraries = Array.from(guestMap.values()).sort(
        (a, b) => a.guestNumber - b.guestNumber
      );

      if (item.packageBasketItemId) {
        const packageItem = nonFulfilableItems.find(
          (booking) => booking.id === item.packageBasketItemId
        );
        packageItem?.guestDetails?.forEach((guest) => {
          const guestKey = getGuestKey(guest.title, guest.firstName, guest.lastName);
          const guestItinerary = guestMap.get(guestKey);
          if (guestItinerary) {
            guestItinerary.items[formattedDate] = guestItinerary.items[formattedDate] || [];
            guestItinerary.items[formattedDate].push(diningItem);
          }
        });
      } else {
        const numberOfDiningGuests = item.adults || 0;

        sortedGuestItineraries.slice(0, numberOfDiningGuests).forEach((guestItinerary) => {
          guestItinerary.items[formattedDate] = guestItinerary.items[formattedDate] || [];
          guestItinerary.items[formattedDate].push(diningItem);
        });
      }
    },
  };

  nonFulfilableItems.forEach((item) => {
    const guests = item.guestDetails || [];
    guests.forEach((guest) => {
      const guestKey = getGuestKey(guest.title, guest.firstName, guest.lastName);
      if (!guestMap.has(guestKey)) {
        guestMap.set(guestKey, {
          guestNumber: 0,
          guestName: formatGuestName(guest),
          items: {},
          hasItems: false,
        });
      }
    });
  });

  const firstBooking = [...nonFulfilableItems]
    .filter((item) => item.type === 'rooms')
    .sort((a, b) => moment(a.checkIn).diff(moment(b.checkIn)))[0];

  const primaryGuest = firstBooking?.guestDetails?.[0];
  const primaryGuestKey = primaryGuest
    ? getGuestKey(primaryGuest.title, primaryGuest.firstName, primaryGuest.lastName)
    : null;

  const sortedGuestKeys = Array.from(guestMap.keys()).sort((a, b) => {
    if (a === primaryGuestKey) return -1;
    if (b === primaryGuestKey) return 1;
    return 0;
  });

  sortedGuestKeys.forEach((guestKey, index) => {
    const guestItinerary = guestMap.get(guestKey);
    if (guestItinerary) {
      guestItinerary.guestNumber = index + 1;
    }
  });

  nonFulfilableItems.forEach((item) => {
    if (item.type === 'rooms') handlers.rooms(item);
    if (item.type?.includes('spa')) handlers.spa(item);
    if (item.type === 'tables') handlers.tables(item);
  });

  return Array.from(guestMap.values())
    .sort((a, b) => a.guestNumber - b.guestNumber)
    .map((guestItinerary) => {
      const sortedItems = Object.fromEntries(
        Object.entries(guestItinerary.items)
          .sort(([dateA], [dateB]) => {
            const momentA = moment(dateA, 'DD MMMM YYYY');
            const momentB = moment(dateB, 'DD MMMM YYYY');
            return momentA.diff(momentB);
          })
          .map(([date, dateItems]) => [
            date,
            dateItems.sort((a, b) => {
              const timeA = moment(a.time, 'HH:mm');
              const timeB = moment(b.time, 'HH:mm');

              if (timeA.isSame(timeB)) {
                const typeOrder = {
                  'room-checkin': 0,
                  spa: 1,
                  dining: 2,
                  'room-checkout': 3,
                };
                return typeOrder[a.type] - typeOrder[b.type];
              }

              return timeA.diff(timeB);
            }),
          ])
      );

      return {
        ...guestItinerary,
        items: sortedItems,
        hasItems: Object.values(sortedItems).some((items) => items.length > 0),
      };
    });
}

const getGuestKey = (title: string, firstName: string, lastName: string) => {
  return `${title}_${firstName}_${lastName}`;
};
